import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/** Poll interval for open ticket thread (ms). */
export const SUPPORT_THREAD_POLL_MS = 4000;

/** Poll interval for admin support queue (ms). */
export const SUPPORT_QUEUE_POLL_MS = 15000;

/** Full queue reload interval as safety reconcile (ms). */
export const SUPPORT_QUEUE_FULL_RELOAD_MS = 180000;

export function mergeThreadMessages(prev, incoming) {
  const byId = new Map((prev || []).map((m) => [m.id, m]));
  for (const m of incoming || []) {
    if (!m?.id) continue;
    byId.set(m.id, m);
  }
  return [...byId.values()].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
  );
}

export function maxMessageCursor(messages) {
  if (!messages?.length) return '';
  return messages.reduce((best, m) => {
    const ts = m?.created_at || '';
    return ts > best ? ts : best;
  }, '');
}

export function maxQueueCursor(tickets, serverTime) {
  let best = serverTime || '';
  for (const t of tickets || []) {
    for (const field of [t?.updated_at, t?.last_message_at]) {
      if (field && field > best) best = field;
    }
  }
  return best;
}

export function mergeQueueTickets(prev, incoming) {
  if (!incoming?.length) return prev || [];
  const byId = new Map((prev || []).map((t) => [t.id, t]));
  for (const t of incoming) {
    if (!t?.id) continue;
    byId.set(t.id, { ...(byId.get(t.id) || {}), ...t });
  }
  const merged = (prev || []).map((t) => byId.get(t.id) || t);
  return [...merged].sort(
    (a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0),
  );
}

export function countInboundMessages(messages) {
  return (messages || []).filter(
    (m) => m.sender_type !== 'admin' && m.sender_type !== 'internal',
  ).length;
}

export function makeOptimisticMessage({
  ticketId, senderType, senderName, senderRole, message,
}) {
  return {
    id: `pending_${Date.now()}`,
    ticket_id: ticketId,
    sender_type: senderType,
    sender_name: senderName,
    sender_role: senderRole || senderType,
    message,
    attachments: [],
    created_at: new Date().toISOString(),
    _pending: true,
  };
}

/**
 * SSE-first thread sync with HTTP poll fallback (P2).
 * Tries EventSource when `streamUrl` is set; falls back to poll on error or unsupported.
 */
export function useSupportThreadRealtime({
  ticketId,
  streamUrl,
  pollUrl,
  enabled,
  paused = false,
  getSince,
  onPollResult,
  preferStream = true,
}) {
  const [streamLive, setStreamLive] = useState(false);
  const onPollRef = useRef(onPollResult);
  onPollRef.current = onPollResult;

  useEffect(() => {
    if (!enabled || paused || !preferStream || !streamUrl || !ticketId) {
      setStreamLive(false);
      return undefined;
    }
    if (typeof EventSource === 'undefined') {
      setStreamLive(false);
      return undefined;
    }

    let es;
    let closed = false;
    try {
      es = new EventSource(streamUrl, { withCredentials: true });
    } catch {
      setStreamLive(false);
      return undefined;
    }

    es.onopen = () => {
      if (!closed) setStreamLive(true);
    };
    es.onmessage = (ev) => {
      if (!ev.data) return;
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'connected') return;
        if (data.type === 'message' && data.message) {
          onPollRef.current?.({
            messages: [data.message],
            ticket: data.ticket,
          });
        }
      } catch {
        /* ignore malformed events */
      }
    };
    es.onerror = () => {
      if (!closed) {
        setStreamLive(false);
        es.close();
      }
    };

    return () => {
      closed = true;
      setStreamLive(false);
      es.close();
    };
  }, [ticketId, streamUrl, enabled, paused, preferStream]);

  useSupportThreadPoll({
    ticketId,
    pollUrl,
    enabled: enabled && !streamLive,
    paused,
    getSince,
    onPollResult,
  });

  return { streamLive };
}

/**
 * Poll incremental messages while a ticket detail view is open.
 * Pauses when the browser tab is hidden or `paused` is true (e.g. modals open).
 */
export function useSupportThreadPoll({
  ticketId,
  pollUrl,
  enabled,
  paused = false,
  getSince,
  onPollResult,
}) {
  const onPollRef = useRef(onPollResult);
  onPollRef.current = onPollResult;
  const getSinceRef = useRef(getSince);
  getSinceRef.current = getSince;

  useEffect(() => {
    if (!enabled || paused || !ticketId || !pollUrl) return undefined;

    const tick = async () => {
      if (document.hidden) return;
      try {
        const r = await axios.get(pollUrl, {
          params: { since: getSinceRef.current() },
          withCredentials: true,
        });
        const incoming = r.data?.messages || [];
        if (incoming.length || r.data?.ticket) {
          onPollRef.current?.(r.data);
        }
      } catch {
        /* ignore transient poll errors */
      }
    };

    tick();
    const iv = setInterval(tick, SUPPORT_THREAD_POLL_MS);
    const onVis = () => {
      if (!document.hidden && !paused) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ticketId, pollUrl, enabled, paused]);
}

/** Auto-scroll thread when user is already at the bottom; track unread while scrolled up. */
export function useThreadScroll(messages) {
  const threadRef = useRef(null);
  const atBottomRef = useRef(true);
  const prevLenRef = useRef(0);
  const [newCount, setNewCount] = useState(0);

  const onThreadScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottomRef.current) setNewCount(0);
  }, []);

  useEffect(() => {
    const len = messages?.length || 0;
    const prev = prevLenRef.current;
    if (len > prev && prev > 0 && !atBottomRef.current) {
      setNewCount((c) => c + (len - prev));
    }
    prevLenRef.current = len;
    if (atBottomRef.current && threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
      setNewCount(0);
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    atBottomRef.current = true;
    setNewCount(0);
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, []);

  return { threadRef, onThreadScroll, scrollToBottom, atBottomRef, newCount };
}

/** Flash document title when customer messages arrive on an open ticket. */
export function useSupportTabTitle({ enabled, ticketRef, ticketStatus, inboundPing }) {
  const savedTitleRef = useRef('');

  useEffect(() => {
    if (!enabled) return undefined;
    savedTitleRef.current = document.title;
    return () => {
      document.title = savedTitleRef.current || document.title;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const base = savedTitleRef.current || 'Support';
    const ref = ticketRef || '';
    const alert = inboundPing > 0 || ticketStatus === 'need_reply';
    document.title = alert && ref ? `(●) ${ref} — ${base}` : (ref ? `${ref} — ${base}` : base);
  }, [enabled, ticketRef, ticketStatus, inboundPing]);
}

/**
 * Lightweight admin queue poll — merges changed rows + tab counts without full reload.
 */
export function useSupportQueuePoll({
  enabled,
  pollUrl,
  pollParams,
  getSince,
  onPollResult,
}) {
  const onPollRef = useRef(onPollResult);
  onPollRef.current = onPollResult;
  const getSinceRef = useRef(getSince);
  getSinceRef.current = getSince;
  const paramsRef = useRef(pollParams);
  paramsRef.current = pollParams;

  useEffect(() => {
    if (!enabled || !pollUrl) return undefined;

    const tick = async () => {
      if (document.hidden) return;
      try {
        const r = await axios.get(pollUrl, {
          params: { since: getSinceRef.current(), ...paramsRef.current },
          withCredentials: true,
        });
        if (r.data?.tickets?.length || r.data?.counts) {
          onPollRef.current?.(r.data);
        }
      } catch {
        /* ignore */
      }
    };

    tick();
    const iv = setInterval(tick, SUPPORT_QUEUE_POLL_MS);
    const onVis = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, pollUrl, pollParams]);
}
